import React, { useState, useEffect, cloneElement, isValidElement, useLayoutEffect, useId } from 'react'
import { createPortal } from 'react-dom'
import { tv } from 'tailwind-variants'

// The tooltip, in the manner of shadcn/ui: an inverted chip (dark on the light theme, light on the
// dark one) with 12px text and a small arrow pointing at its trigger, easing in from just under
// full size. Long content wraps within a capped width into balanced, centred lines, and the box is
// then narrowed to its widest line: wrapped text otherwise leaves the box at its full width, with
// empty space beside the text. A consumer's className is merged
// (tailwind-merge) onto the outer box, so utilities like `max-w-*` + `whitespace-nowrap` + `w-max`
// override those defaults.
//
// Two layers: the outer box is only positioned and measured, and never transformed, so its size
// is its real size; the inner chip carries the colours, the arrow and the scale-in animation.
const tooltipBox = tv({
  slots: {
    box: 'tooltip-portal fixed text-xs/4 whitespace-pre-wrap break-words max-w-[min(360px,calc(100vw-16px))] pointer-events-none z-[9999]',
    chip: 'relative rounded-md bg-body text-surface px-3 py-1.5 text-balance text-center animate-tooltip-in motion-reduce:animate-none',
    arrow: 'absolute size-2 rotate-45 rounded-[1px] bg-body'
  },
  variants: {
    side: {
      bottom: { chip: 'origin-top', arrow: '-top-1' },
      top: { chip: 'origin-bottom', arrow: '-bottom-1' },
      // Squeezed into a window it doesn't fit either side of: nothing to point at.
      none: { arrow: 'hidden' }
    }
  }
})

interface ChildProps {
  'aria-describedby'?: string
  onMouseEnter?: (e: React.MouseEvent) => void
  onMouseLeave?: (e: React.MouseEvent) => void
  onMouseDown?: (e: React.MouseEvent) => void
}

interface TooltipProps {
  content: string
  children: React.ReactElement<ChildProps>
  /** If set, wait this many ms before showing on hover. Cleared on mouse leave. Default 0 (immediate). */
  delay?: number
  /**
   * If true, only show when the trigger element's text is actually cut short: wider than its box (an
   * ellipsis on one line) or taller than it (clamped to a few lines).
   */
  showOnlyIfTruncated?: boolean
  /**
   * Controlled visibility. When provided, hover is ignored and the tooltip shows iff this is true.
   * Use when the trigger element can't emit mouse events (e.g. during a native HTML5 drag, which
   * suppresses mouseenter/mouseleave) but its visibility is driven by external state instead.
   */
  visible?: boolean
  /**
   * Extra classes for the tooltip box, merged over the defaults. Pass width/wrapping utilities
   * (e.g. `w-max whitespace-normal max-w-[320px]`) to widen it, or `whitespace-nowrap` to keep it
   * on a single line.
   */
  className?: string
}

/** Gap between the trigger element and the tooltip: room for the arrow's tip, and a little air. */
const TRIGGER_GAP = 8
/** Minimum gap kept between the tooltip and the edge of the window. */
const VIEWPORT_PADDING = 8
/** How close the arrow may come to the tooltip's corners, which are rounded. */
const ARROW_INSET = 10
/** Half the arrow's width, to centre it on the point it aims at. */
const ARROW_HALF = 4
/** The chip's padding either side of its text (px-3). */
const CHIP_PADDING_X = 24

/** Where the tooltip sits, in pixels, and which side of it the arrow is on. */
interface Placement {
  top: number
  left: number
  side: 'top' | 'bottom' | 'none'
  /** The arrow's centre, from the tooltip's left edge: under the trigger's centre. */
  arrowLeft: number
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max))

/** Announced on the document as a hover tooltip shows, with its id, so any other one showing hides. */
const TOOLTIP_OPEN = 'tooltip.open'

const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  delay = 0,
  showOnlyIfTruncated = false,
  visible,
  className
}) => {
  const isControlled = visible !== undefined
  const [hoverVisible, setHoverVisible] = useState(false)
  const shown = isControlled ? (visible as boolean) : hoverVisible
  // The trigger's centre, the top just below it, and its own top (for flipping above).
  const [position, setPosition] = useState({ top: 0, left: 0, triggerTop: 0 })
  // Null until the tooltip has been measured where it's shown; see the layout effect below.
  const [placement, setPlacement] = useState<Placement | null>(null)
  // Wrapped text: the box's width narrowed to its widest line, before it's placed. Null while it's
  // measured at its natural width.
  const [fitWidth, setFitWidth] = useState<number | null>(null)
  const [triggerEl, setTriggerEl] = useState<HTMLElement | null>(null)
  const [tooltipEl, setTooltipEl] = useState<HTMLDivElement | null>(null)
  const [textEl, setTextEl] = useState<HTMLSpanElement | null>(null)
  const [hovering, setHovering] = useState(false)
  // Ties the tooltip to its trigger for screen readers, while it shows.
  const tooltipId = useId()

  const remeasure = () => {
    setPlacement(null)
    setFitWidth(null)
  }

  const anchorTo = (el: HTMLElement) => {
    const rect = el.getBoundingClientRect()
    setPosition({
      top: rect.bottom + TRIGGER_GAP,
      left: rect.left + rect.width / 2,
      triggerTop: rect.top
    })
    remeasure()
  }

  // The show delay is owned by this effect rather than a timer ref: the hover handlers are handed
  // to cloneElement in a props object that the React Compiler cannot prove is a set of event
  // handlers, so reading a ref from them counts as a render-time ref access and breaks compilation.
  useEffect(() => {
    if (isControlled || !hovering || !triggerEl) return
    if (
      showOnlyIfTruncated &&
      triggerEl.scrollWidth <= triggerEl.clientWidth &&
      triggerEl.scrollHeight <= triggerEl.clientHeight
    )
      return

    const reveal = () => {
      anchorTo(triggerEl)
      setHoverVisible(true)
    }

    if (delay <= 0) {
      reveal()
      return
    }

    const showTimer = window.setTimeout(reveal, delay)
    return () => window.clearTimeout(showTimer)
  }, [isControlled, hovering, triggerEl, delay, showOnlyIfTruncated])

  // In controlled mode there are no mouse events to drive positioning, so anchor the
  // tooltip to the trigger whenever it becomes visible.
  useLayoutEffect(() => {
    if (!isControlled || !visible || !triggerEl) return
    anchorTo(triggerEl)
  }, [isControlled, visible, triggerEl])

  // Taken over by its parent (say, hidden while a menu it names is open), a tooltip forgets any
  // hover: in controlled mode it doesn't hear the pointer leave, so on being handed back it would
  // otherwise show again with the pointer long gone.
  useLayoutEffect(() => {
    if (isControlled) {
      setHovering(false)
      setHoverVisible(false)
    }
  }, [isControlled])

  // New text, or a new width, while it's showing: measure and place it again.
  useLayoutEffect(() => {
    remeasure()
  }, [content, className])

  // Controlled mode never runs hideTooltip, so drop the placement when it hides: the next show
  // measures afresh rather than reusing it.
  useLayoutEffect(() => {
    if (isControlled && !visible) {
      remeasure()
    }
  }, [isControlled, visible])

  const hideTooltip = () => {
    setHovering(false)
    setHoverVisible(false)
    remeasure()
  }

  // Browsers don't always report the pointer leaving: a trigger moved in the page while hovered, or
  // a window or tab switched away from, never hears it, and its tooltip would stay up (and pile up
  // with others, #11055). So, as Radix's tooltip does (the one shadcn's is built on), a hover
  // tooltip also hides when another shows, on Escape, when the window loses focus or the tab is
  // hidden, and when something it sits in scrolls (it's placed once, so it would be left behind).
  // From the pointer arriving, not from showing: one still waiting out its delay is called off too,
  // rather than showing later over a tooltip shown since.
  useEffect(() => {
    if (isControlled || !hovering) return

    const onOtherOpen = (event: Event) => {
      if ((event as CustomEvent<string>).detail !== tooltipId) hideTooltip()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hideTooltip()
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') hideTooltip()
    }
    const onScroll = (event: Event) => {
      if (triggerEl && event.target instanceof Node && event.target.contains(triggerEl)) hideTooltip()
    }

    document.addEventListener(TOOLTIP_OPEN, onOtherOpen)
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('blur', hideTooltip)
    // Captured: scroll events don't bubble.
    window.addEventListener('scroll', onScroll, { capture: true })
    return () => {
      document.removeEventListener(TOOLTIP_OPEN, onOtherOpen)
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('blur', hideTooltip)
      window.removeEventListener('scroll', onScroll, { capture: true })
    }
  }, [isControlled, hovering, tooltipId, triggerEl])

  useEffect(() => {
    if (!isControlled && hoverVisible) {
      document.dispatchEvent(new CustomEvent(TOOLTIP_OPEN, { detail: tooltipId }))
    }
  }, [isControlled, hoverVisible, tooltipId])

  // Place the tooltip once it's measured, before it's painted: first drawn, unseen, at the window's
  // left edge, where nothing squeezes its width, then centred under the trigger and kept inside the
  // window. It's measured whenever it has no placement: on showing, and when its text changes. Wrapped content can be tall enough to run off the bottom; it flips above the trigger
  // when there's room there, otherwise it's clamped back into view with no arrow. The arrow stays
  // under the trigger's centre even when the tooltip is pushed in from an edge.
  useLayoutEffect(() => {
    if (!shown || !tooltipEl || placement) return

    // Text that wraps keeps the box at its maximum width, however short its lines: narrow it to
    // the widest line (an inline element has a rect per line), and place it once drawn that wide.
    // The chip is still scaling in, and rects are measured after transforms: undo its scale.
    if (fitWidth === null && textEl) {
      const lines = Array.from(textEl.getClientRects())
      if (lines.length > 1) {
        const chip = textEl.parentElement
        const scale = chip?.offsetWidth ? chip.getBoundingClientRect().width / chip.offsetWidth : 1
        const widest = Math.max(...lines.map((line) => line.width)) / scale
        setFitWidth(Math.ceil(widest) + CHIP_PADDING_X)
        return
      }
    }

    const { width, height } = tooltipEl.getBoundingClientRect()
    const left = clamp(position.left - width / 2, VIEWPORT_PADDING, window.innerWidth - VIEWPORT_PADDING - width)

    let top = position.top
    let side: Placement['side'] = 'bottom'
    if (top + height > window.innerHeight - VIEWPORT_PADDING) {
      const above = position.triggerTop - height - TRIGGER_GAP
      if (above >= VIEWPORT_PADDING) {
        top = above
        side = 'top'
      } else {
        top = Math.max(VIEWPORT_PADDING, window.innerHeight - height - VIEWPORT_PADDING)
        side = 'none'
      }
    }

    // Whole pixels, so the text isn't blurred by a fractional offset.
    const arrowLeft = clamp(position.left - left, ARROW_INSET, width - ARROW_INSET)
    setPlacement({ top: Math.round(top), left: Math.round(left), side, arrowLeft: Math.round(arrowLeft) })
  }, [shown, position, tooltipEl, textEl, placement, fitWidth])

  if (!isValidElement(children)) {
    return children
  }

  const child = cloneElement(children, {
    ref: setTriggerEl,
    // Adds to any description the trigger already has, rather than replacing it.
    'aria-describedby': shown
      ? [children.props['aria-describedby'], tooltipId].filter(Boolean).join(' ')
      : children.props['aria-describedby'],
    // Always forward the child's own hover handlers; only gate Tooltip's own
    // show/hide logic on uncontrolled mode so controlled consumers keep their events.
    onMouseEnter: (e: React.MouseEvent) => {
      if (!isControlled) {
        setHovering(true)
      }
      if (children.props.onMouseEnter) {
        children.props.onMouseEnter(e)
      }
    },
    onMouseLeave: (e: React.MouseEvent) => {
      if (!isControlled) {
        hideTooltip()
      }
      if (children.props.onMouseLeave) {
        children.props.onMouseLeave(e)
      }
    },
    // Pressing the trigger puts the tooltip away, and it stays away until the pointer leaves and
    // comes back: a button that opens a menu would otherwise keep its tooltip over the menu.
    onMouseDown: (e: React.MouseEvent) => {
      if (!isControlled) {
        hideTooltip()
      }
      if (children.props.onMouseDown) {
        children.props.onMouseDown(e)
      }
    }
  } as any)

  const styles = tooltipBox({ side: placement?.side ?? 'bottom' })

  return (
    <>
      {child}
      {shown &&
        createPortal(
          <div
            ref={setTooltipEl}
            role="tooltip"
            id={tooltipId}
            className={styles.box({ class: className })}
            style={
              placement
                ? { top: placement.top, left: placement.left, width: fitWidth ?? undefined }
                : { top: position.top, left: 0, width: fitWidth ?? undefined, visibility: 'hidden' }
            }
          >
            <div className={styles.chip()}>
              <span
                className={styles.arrow()}
                style={{ left: (placement?.arrowLeft ?? ARROW_INSET) - ARROW_HALF }}
                aria-hidden
              />
              <span ref={setTextEl}>{content}</span>
            </div>
          </div>,
          document.body
        )}
    </>
  )
}

export default Tooltip
